/**
 * Fits the labels of the FALC principle wheel.
 *
 * Everything inside the SVG is laid out in viewBox units, so the fit depends
 * only on the (CMS driven) label texts, not on the viewport. Once fonts are
 * ready we pick the largest font size (in viewBox units) at which
 *   - every label fits its foreignObject box, and
 *   - no two rendered label texts collide (neighbours near the top/bottom of
 *     the wheel share most of their horizontal range).
 * Measuring is done in the bold hover state so hovering never breaks the fit.
 *
 * If even the minimum size collides, each label is shortened word by word
 * (with an ellipsis) to the height its neighbours leave room for; the SVG
 * <title> still shows the full text on hover and the mobile list is untouched.
 */
export function principleWheel() {
	const wheels = document.querySelectorAll('.falc-principle__wheel svg')
	if (!wheels.length) return

	const MAX_SIZE = 13 // viewBox units
	const MIN_SIZE = 9
	const STEP = 0.5
	const MARGIN = 2 // viewBox units kept free between neighbouring labels

	const overlaps = (a, b, m) =>
		a.left < b.right + m && b.left < a.right + m && a.top < b.bottom + m && b.top < a.bottom + m

	const textRect = (el) => {
		const range = document.createRange()
		range.selectNodeContents(el)
		return range.getBoundingClientRect()
	}

	const fit = (svg) => {
		const svgWidth = svg.getBoundingClientRect().width
		if (!svgWidth) return false // hidden (mobile list is shown instead)

		const scale = svgWidth / svg.viewBox.baseVal.width
		const labels = [...svg.querySelectorAll('foreignObject')]
			.map((fo) => ({
				fo,
				w: parseFloat(fo.getAttribute('width')),
				h: parseFloat(fo.getAttribute('height')),
				text: fo.querySelector('.falc-principle__label-text'),
			}))
			.filter((l) => l.text)
		if (!labels.length) return true

		svg.classList.add('is-measuring')

		const setSize = (size) => svg.style.setProperty('--falc-label-size', `${size}px`)
		const tooBig = (l, maxH) => l.text.scrollWidth > l.w || textRect(l.text).height / scale > maxH

		const ok = () => {
			const rects = labels.map((l) => textRect(l.text))
			for (let i = 0; i < labels.length; i++) {
				if (tooBig(labels[i], labels[i].h)) return false
				for (let j = i + 1; j < labels.length; j++) {
					if (overlaps(rects[i], rects[j], MARGIN * scale)) return false
				}
			}
			return true
		}

		let size = MAX_SIZE
		setSize(size)
		while (!ok() && size - STEP >= MIN_SIZE) {
			size -= STEP
			setSize(size)
		}

		if (!ok()) {
			// Last resort: shorten every label to the vertical room its neighbours
			// (boxes sharing horizontal range) leave, centred on its anchor.
			const boxes = labels.map((l) => l.fo.getBoundingClientRect())
			labels.forEach((l, i) => {
				const a = boxes[i]
				const cy = (a.top + a.bottom) / 2
				let allowance = l.h
				boxes.forEach((b, j) => {
					if (j === i || a.left >= b.right || b.left >= a.right) return
					const dy = Math.abs(cy - (b.top + b.bottom) / 2) / scale
					allowance = Math.min(allowance, dy - MARGIN)
				})
				const words = l.text.textContent.trim().split(/\s+/)
				while (tooBig(l, allowance) && words.length > 1) {
					words.pop()
					l.text.textContent = `${words.join(' ')}…`
				}
			})
		}

		svg.classList.remove('is-measuring')
		return true
	}

	const run = () =>
		wheels.forEach((svg) => {
			if (svg.dataset.fitted === 'true') return
			if (fit(svg)) svg.dataset.fitted = 'true'
		})

	const ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()
	ready.then(run)

	// The wheel is display:none below the small breakpoint; fit once it becomes visible.
	window.matchMedia('(min-width: 48rem)').addEventListener('change', () => ready.then(run))
}
